<script lang="ts">
    import { DEFAULT_BOX, DEFAULT_LAYOUT } from '$lib/components/constants';

    import { getBorderRadius } from '$lib/global/helper/border-radius';
    import { cn } from '$lib/utils/cn';

    import Color from 'color';

    import { getGradientColors } from '../_utils/color';
    import { getBoxAttributes } from '../_utils/getBoxAttributes';
    import Box from '../_wrappers/Box.svelte';
    import Text from '../Text/Text.View.svelte';

    import type { ButtonStyle } from '$lib/types/Components/Button/ButtonContent';
    import type { Box as BoxType } from '$lib/types/Components/SharedTypes';
    import type { Align } from '$lib/types/Components/SharedTypes';
    import type { Language } from '$lib/types/Language';
    import type { TransitionEffect } from '$lib/types/Theme';
    import type { JSONContent } from '@tiptap/core';

    export let id = '';
    export let draft: JSONContent;
    export let wysiwygHtml: string | undefined = undefined;
    export let bgColor = '#0076FF';
    export let color = '#FFFFFF';
    export let align: Align = 'center';
    export let animation: string;
    export let loading = false;
    export let disabled = false;
    export let onClick: () => void = () => {};
    export let onMouseEnter: (event: MouseEvent | TouchEvent) => void;
    export let buttonType: 'button' | 'submit' = 'button';
    export let buttonStyle: ButtonStyle = 'filled';
    export let fullWidthOnDesktop = false;
    export let language: Language = 'en';

    export let box: BoxType;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';

    export let isInMediaAnswer = false;
    export let layout = DEFAULT_LAYOUT;

    const { isInColumn } = layout;

    const isGradient = bgColor.includes('linear-gradient');

    const fillColor = buttonStyle === 'filled' ? bgColor : 'transparent';
    const outlineColor = isGradient ? getGradientColors(bgColor)[0] : bgColor;

    const bgColorDark = isGradient || buttonStyle === 'outline' ? '' : Color(fillColor).darken(0.1);
    const outlineHoverBgColor = Color(outlineColor).alpha(0.1).hexa();

    const buttonClasses = cn(
        'fun-box-border fun-block fun-w-full fun-cursor-pointer fun-bg-no-repeat fun-px-6 fun-py-4 fun-shadow-button-inset fun-transition-all lg:fun-px-8',
        'fun-text-[var(--buttonColor)]',
        getBorderRadius('button'),
        {
            'fun-bg-[var(--buttonBgColor)] sm:hover:fun-bg-[var(--buttonBgColorDark)] sm:active:fun-bg-[var(--buttonBgColorDark)]':
                !isGradient && buttonStyle === 'filled',
            'fun-animate-button-jittery': animation === 'jittery',
            'fun-text-left': align === 'left',
            'fun-mx-auto fun-text-center': align === 'center',
            'lg:fun-w-auto': !fullWidthOnDesktop,
            'lg:fun-w-full': fullWidthOnDesktop,
            'fun-transform-none fun-cursor-not-allowed fun-opacity-50': disabled,
            'fun-transform active:fun-scale-90 active:fun-duration-200': !disabled,
            'fun-shadow-none fun-outline fun-outline-[1.5px] fun--outline-offset-[1.5px] fun-outline-[var(--outlineColor)] sm:hover:fun-bg-[var(--outlineHoverBgColor)]':
                buttonStyle === 'outline',
            'fun-bg-[length:100%] sm:fun-duration-500 sm:hover:fun-bg-[length:200%]':
                isGradient && buttonStyle === 'filled',
        },
    );

    const backgroundImageStyle =
        isGradient && buttonStyle === 'filled' ? `background-image: ${fillColor};` : '';
</script>

<Box
    {id}
    {...getBoxAttributes({ box, isInColumn, isInMediaAnswer })}
    {transitionDelay}
    {transitionEffect}
>
    <button
        {id}
        class={buttonClasses}
        type={buttonType}
        style="--buttonColor: {color}; --buttonBgColor: {fillColor}; --buttonBgColorDark: {bgColorDark}; --outlineColor: {outlineColor}; --outlineHoverBgColor: {outlineHoverBgColor}; {backgroundImageStyle}"
        on:click={loading || disabled ? undefined : onClick}
        on:mouseenter={onMouseEnter}
        on:touchstart={onMouseEnter}
        {...$$restProps}
        aria-label={loading ? (language === 'de' ? 'Lädt...' : 'Loading...') : undefined}
        aria-disabled={disabled}
    >
        {#if loading}
            <div
                class="fun-pointer-events-none fun-flex fun-h-[21px] fun-items-center fun-justify-center"
            >
                <span
                    class="fun-block fun-size-4 fun-animate-button-spinner fun-rounded-full fun-border-2 fun-border-gray-200 fun-border-r-transparent fun-border-t-transparent"
                />
            </div>
        {:else}
            <Text
                raw={draft}
                {wysiwygHtml}
                {align}
                {color}
                box={DEFAULT_BOX}
                transitionEffect="none"
            />
        {/if}
    </button>
</Box>
